<script setup lang="ts">
import type { ParsedKevelAd } from '../../utils/parseKevelDecision'

interface Props {
    ad: ParsedKevelAd
    aboveTheFold?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    aboveTheFold: false
})

const { t } = useI18n()

// Kevel tracking (impression + click)
const { trackClick, isValidTarget } = useKevelAdTracking(props.ad, {
    component: 'cart-teaser',
})

// Handle container click
function handleContainerClick(event: MouseEvent | KeyboardEvent) {
    if (isValidTarget) {
        trackClick(event instanceof MouseEvent ? event : undefined)
    }
}
</script>

<template>
    <div
        class="kevel-ad-cart-teaser block relative bg-base-200"
        :class="{ 'cursor-pointer': isValidTarget }"
        :role="isValidTarget ? 'button' : undefined"
        :tabindex="isValidTarget ? 0 : undefined"
        v-on="isValidTarget ? {
            click: handleContainerClick,
            'keydown.enter': handleContainerClick,
            'keydown.space': (e: KeyboardEvent) => { e.preventDefault(); handleContainerClick(e); }
        } : {}"
    >
        <!-- Sponsored Label (top-right) -->
        <div class="absolute top-2 right-2 z-10">
            <p class="text-xs uppercase font-normal text-base-content-subtle tracking-[0.5px] bg-base-100 px-2 py-1">
                {{ t('kevel.ad.label') }}
            </p>
        </div>

        <!-- 6:1 Image -->
        <MiscImage
            :src="ad.assetUrl"
            :alt="ad.headline ?? t('kevel.ad.defaultAlt')"
            class="w-full aspect-[4/2] md:aspect-[6/1] object-cover"
            :lazy-load="!aboveTheFold"
            :preload="aboveTheFold ? { fetchPriority: 'high' } : false"
        />
    </div>
</template>
